import React, { useState, useEffect } from 'react';
import { motion, AnimateSharedLayout } from 'framer-motion';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { HiOutlineX} from 'react-icons/hi';
import Chart from 'react-apexcharts';
import {
  getPrestamosPorRol,
  getPrestamosPorCarrera,
  getPrestamosPorFondo,
  getPrestamosPorCategoriaDewey
} from '../../../services/EstadisticasService';
// En la parte superior de tu archivo Card.js o ExpandedCard.js
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns'; // Asegúrate de importar format si no lo has hecho
import { acortarTexto } from '../../../utils/functions';

import './index.css';

function Card(props) {
    const [expanded, setExpanded] = useState(false);

    const [summaryData, setSummaryData] = useState(null);

    useEffect(() => {
      const fetchSummaryData = async () => {
        try {
          // Puedes utilizar un rango de fechas predeterminado o el mismo que en ExpandedCard
          const formattedStartDate = format(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd');
          const formattedEndDate = format(new Date(), 'yyyy-MM-dd');
  
          let data;
          if (props.title === 'Préstamos por Rol') {
            data = await getPrestamosPorRol(formattedStartDate, formattedEndDate);
          } else if (props.title === 'Préstamos por Carrera') {
            data = await getPrestamosPorCarrera(formattedStartDate, formattedEndDate);
          } else if (props.title === 'Préstamos por Fondos') {
            data = await getPrestamosPorFondo(formattedStartDate, formattedEndDate);
          }
  
          if (data && data.length > 0) {
            // Obtener el elemento con mayor cantidad
            const maxItem = data.reduce((prev, current) => (prev.cantidad > current.cantidad) ? prev : current);
            const total = data.reduce((sum, item) => sum + item.cantidad, 0);
            const percentage = ((maxItem.cantidad / total) * 100).toFixed(1);
  
            setSummaryData({
              name: maxItem.nombre,
              count: maxItem.cantidad,
              percentage,
            });
          }
        } catch (error) {
          console.error(`Error al obtener datos para ${props.title}:`, error);
        }
      };
  
      fetchSummaryData();
    }, []);
  
    return (
      <AnimateSharedLayout>
        {
          expanded ?
            <ExpandedCard param={props} setExpanded={() => setExpanded(false)} /> :
            <CompactCard param={props} setExpanded={() => setExpanded(true)} summaryData={summaryData} />
        }
      </AnimateSharedLayout>
    );
}

export default Card

function CompactCard({param, setExpanded, summaryData}){
    const Png = param.png;
    return(
        <motion.div className='compact-card-container' style={{
            background:param.color.backGround,
            boxShadow: param.color.boxShadow
        }}
        onClick={setExpanded}
        layoutId='expandableCard'
        >
          {summaryData ? (
            <>
              <div className='radial-bar'>
                <CircularProgressbar
                value={summaryData.percentage}
                text={`${summaryData.percentage}%`}
                />
                <span>{param.title}</span>
              </div>
              <div className='detail'>
                  <Png size={24}/>
                  <span>{acortarTexto(summaryData.name, 14)}</span>
                  <span>{summaryData.count}</span>
                  <span>Últimos 7 días</span>
              </div>
            </>

          ): (
            <span>Cargando...</span>
          )}
            
        </motion.div>
    )
}

function ExpandedCard({ param, setExpanded }) {
  const [chartOptions, setChartOptions] = useState({});
  const [chartSeries, setChartSeries] = useState([]);
  const [chartData, setChartData] = useState(null);

  // Estados para las fechas
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
  const [endDate, setEndDate] = useState(new Date());

  // Estado para el tipo de clasificación (aplicable para 'Préstamos por Fondos')
  const [clasificacion, setClasificacion] = useState('fondo'); // 'fondo' o 'tematica'

  const colores = ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#3F51B5', '#03A9F4', '#4CAF50', '#F9C80E', '#FF9800'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const formattedStartDate = format(startDate, 'yyyy-MM-dd');
        const formattedEndDate = format(endDate, 'yyyy-MM-dd');
        let data;

        if (param.title === 'Préstamos por Rol') {
          data = await getPrestamosPorRol(formattedStartDate, formattedEndDate);
          const labels = data.map(item => item.nombre);
          const series = data.map(item => item.cantidad);

          setChartOptions({
            labels: labels,
            chart: {
              type: 'pie',
              toolbar: {
                show: true,
              },
            },
            title: {
              show: false,
            },
            legend: {
              position: 'bottom',
            },
          });
          setChartSeries(series);
        } else if (param.title === 'Préstamos por Carrera') {
          data = await getPrestamosPorCarrera(formattedStartDate, formattedEndDate);
          const categories = data.map(item => item.nombre);
          const seriesData = data.map(item => item.cantidad);

          setChartOptions({
            chart: {
              type: 'bar',
            },
            xaxis: {
              categories: categories,
              labels: {
                show: false, // Ocultamos las etiquetas si son muy largas
              },
            },
            colors: colores,
            title: {
              show: false,
            },
            plotOptions: {
              bar: {
                distributed: true,
              },
            },
          });
          setChartSeries([
            {
              name: 'Préstamos',
              data: seriesData,
            },
          ]);
        } else if (param.title === 'Préstamos por Fondos') {
          if (clasificacion === 'fondo') {
            data = await getPrestamosPorFondo(formattedStartDate, formattedEndDate);
            const labels = data.map(item => item.nombre);
            const series = data.map(item => item.cantidad);

            setChartOptions({
              labels: labels,
              chart: {
                type: 'pie',
                toolbar: {
                  show: true,
                },
              },
              title: {
                show: false,
              },
              legend: {
                position: 'bottom',
              },
            });
            setChartSeries(series);
          } else if (clasificacion === 'tematica') {
            data = await getPrestamosPorCategoriaDewey(formattedStartDate, formattedEndDate);
            const labels = data.map(item => `${item.codigoDewey} - ${item.nombre}`);
            const series = data.map(item => item.cantidad);

            setChartOptions({
              labels: labels,
              chart: {
                type: 'pie',
                toolbar: {
                  show: true,
                },
              },
              title: {
                show: false,
              },
              legend: {
                position: 'bottom',
              },
            });
            setChartSeries(series);
          }
        }

        if (data && data.length > 0) {
          setChartData(data);
        } else {
          setChartData([]); // No hay datos
        }
      } catch (error) {
        console.error(`Error al obtener datos para ${param.title}:`, error);
        setChartData([]); // En caso de error, establecemos chartData como vacío
      }
    };

    fetchData();
  }, [startDate, endDate, clasificacion]); // Agregamos 'clasificacion' a las dependencias

  return (
    <motion.div
      className='expanded-card-container'
      style={{
        background: param.color.backGround,
        boxShadow: param.color.boxShadow,
      }}
      layoutId='expandableCard'
    >
      <div style={{ alignSelf: 'flex-end', cursor: 'pointer', color: 'white' }}>
        <HiOutlineX onClick={setExpanded} size={24} />
      </div>
      <span>{param.title}</span>
      <div className='date-selectors-card'>
        <label>Fecha Inicio:</label>
        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          dateFormat='dd/MM/yyyy'
          maxDate={endDate}
        />
        <label>Fecha Fin:</label>
        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          dateFormat='dd/MM/yyyy'
          minDate={startDate}
          maxDate={new Date()}
        />
      </div>
      {param.title === 'Préstamos por Fondos' && (
        <div className='date-selectors-card'>
          <label>Clasificar por:</label>
          <select value={clasificacion} onChange={(e) => setClasificacion(e.target.value)}>
            <option value='fondo'>Fondo</option>
            <option value='tematica'>Temática</option>
          </select>
        </div>
      )}
      <div className='chart-container'>
        {chartData ? (
          chartData.length > 0 ? (
            <Chart options={chartOptions} series={chartSeries} type={chartOptions.chart.type} />
          ) : (
            <span>No hay datos para el rango seleccionado.</span>
          )
        ) : (
          <span>Cargando datos...</span>
        )}
      </div>
    </motion.div>
  );
}

